import { Box, Grid, Typography, Divider } from '@material-ui/core';
import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import Tooltip from '@material-ui/core/Tooltip';
import { useSelector } from 'react-redux';

import { OrderDetailsContext } from '@hooks/useOrderDetails';
import errorIcon from '@assets/error.svg';
import { formatPriceWithCents } from '@utils/currency';
import { getLocalizedContent as l } from '@helpers/languages';
import { PAYMENT_STATUS, PAYMENT_TYPE } from '@constants/payments';
import content from './PaymentHistoryData.json';

const useStyles = makeStyles(({ palette }) => ({
  price: {
    color: palette.charcoal,
  },
  total: {
    fontStretch: 'condensed',
    color: 'var(--Charcoal, #40423F)',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '28px',
    letterSpacing: '0.23px',
  },
  amountDue: {
    fontStretch: 'condensed',
    fontWeight: 500,
    color: 'var(--Nero, #1B1B1B)',
    fontSize: '16px',
    fontStyle: 'normal',
    lineHeight: '28px',
    letterSpacing: '-0.3px',
  },
  amountDueValue: {
    color: 'var(--Charcoal, #40423F)',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '28px',
    letterSpacing: '-0.3px',
    fontStretch: 'condensed',
  },
  error: {
    color: '#FF0000',
  },
  paymentItem: {
    fontStretch: 'condensed',
    textIndent: '1.5rem',
    marginTop: '7px',
    color: 'var(--Charcoal, #40423F)',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '28px',
    letterSpacing: '0.23px',
  },
  desktopDivider: {
    backgroundColor: '#787878',
    opacity: 0.4,
  },
}));

const Error = () => {
  const classes = useStyles();
  const { prefixLanguage } = useSelector(({ appState }) => appState);
  const language = prefixLanguage || 'en';

  return (
    <Box display="flex">
      <img src={errorIcon} alt="error" style={{ width: '1.5rem' }} />
      <Typography className={classes.error} variant="body1">
        {l(content.error, language)}
      </Typography>
    </Box>
  );
};

const PaymentHistoryData = () => {
  const classes = useStyles();
  const { error, total, balance, payments, sfOrder } =
    useContext(OrderDetailsContext);
  const { prefixLanguage } = useSelector(({ appState }) => appState);
  const language = prefixLanguage || 'en';

  const paymentStatusToString = paymentStatus => {
    if (paymentStatus === 'SUCCEEDED') {
      return '(Successful)';
    } else {
      return (
        '(' +
        String(paymentStatus).charAt(0).toUpperCase() +
        String(paymentStatus).slice(1).toLowerCase().split('_').join(' ') +
        ')'
      );
    }
  };

  return (
    <Grid container spacing={1}>
      {/* Total */}
      <Grid item xs={12} container>
        <Grid item xs>
          <Typography
            variant="body1"
            data-cy="data-total-label"
            className={classes.total}
          >
            {l(content.total, language)}
          </Typography>
        </Grid>
        <Grid item xs container justifyContent="flex-end">
          {error ? (
            <Error />
          ) : (
            <Typography
              variant="body1"
              data-cy="data-total-value"
              className={[classes.price, classes.total].join(' ')}
              data-total
            >
              {formatPriceWithCents(sfOrder.orderTotal, sfOrder.currencyCode)}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} style={{ width: '100%' }}>
        <Divider className={classes.desktopDivider} />
      </Grid>

      {sfOrder.financeApprovedAmount > 0 && (
        <Grid item xs={12} container>
          <Grid item xs>
            <Typography
              variant="body1"
              data-cy="data-financedAmount-label"
              className={classes.total}
            >
              {l(content.financedAmount, language)}
            </Typography>
          </Grid>
          <Grid item xs container justifyContent="flex-end">
            {error ? (
              <Error />
            ) : (
              <Typography
                variant="body1"
                data-cy="data-financedAmount-value"
                className={[classes.price, classes.total].join(' ')}
                data-total
              >
                {'- ' +
                  formatPriceWithCents(
                    sfOrder.financeApprovedAmount,
                    sfOrder.currencyCode
                  )}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}

      {sfOrder.acquisitionAmountRemaining > 0 && (
        <Grid item xs={12} container>
          <Grid item xs>
            <Typography
              variant="body1"
              data-cy="data-tradeinEquity-label"
              className={classes.total}
            >
              {l(content.tradeinEquity, language)}
            </Typography>
          </Grid>
          <Grid item xs container justifyContent="flex-end">
            {error ? (
              <Error />
            ) : (
              <Typography
                variant="body1"
                data-cy="data-tradeinEquity-value"
                className={[classes.price, classes.total].join(' ')}
                data-total
              >
                {'- ' +
                  formatPriceWithCents(
                    sfOrder.acquisitionAmountRemaining,
                    sfOrder.currencyCode
                  )}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}

      {sfOrder.financeApprovedAmount + sfOrder.acquisitionAmountRemaining >
        0 && (
        <Grid item xs={12} style={{ width: '100%' }}>
          <Divider className={classes.desktopDivider} />
        </Grid>
      )}
      {/* <Grid item xs={12} container>
        <Grid item xs>
          <Typography
            variant="body1"
            data-cy="data-deposit-label"
            className={classes.total}
          >
            {l(content.deposit, language)}
          </Typography>
        </Grid>
        <Grid item xs container justifyContent="flex-end">
          {error ? (
            <Error />
          ) : (
            <Typography
              variant="body1"
              data-cy="data-deposit-value"
              className={[classes.price, classes.total].join(' ')}
              data-total
            >
              {'- ' +
                formatPriceWithCents(
                  sfOrder.orderDeposit,
                  sfOrder.currencyCode
                )}
            </Typography>
          )}
        </Grid>
      </Grid> */}

      <Grid item xs={12} container>
        <Grid item xs>
          <Typography
            variant="body1"
            data-cy="data-payments-label"
            className={classes.total}
          >
            {l(content.payments, language)}
          </Typography>
        </Grid>
        <Grid item xs container justifyContent="flex-end">
          {error ? (
            <Error />
          ) : (
            <Typography
              variant="body1"
              data-cy="data-payments-value"
              className={[classes.price, classes.total].join(' ')}
              data-total
            >
              {'- ' +
                formatPriceWithCents(
                  sfOrder.totalPayments,
                  sfOrder.currencyCode
                )}
            </Typography>
          )}
        </Grid>

        {sfOrder.payments?.length &&
          sfOrder.payments
            .filter(p => p.paymentType !== PAYMENT_TYPE.CHARGEBACK)
            .map((payment, idx) => {
              const isFailedPayment = [
                PAYMENT_STATUS.FAILED,
                PAYMENT_STATUS.VOID,
              ].includes(payment.paymentStatus);
              return (
                <Grid key={idx} item xs={12} container>
                  <Grid item xs={4}>
                    <Typography
                      variant="body1"
                      className={classes.paymentItem}
                      data-payment-status
                    >
                      {payment.paymentReason || payment.paymentMethod}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs container justifyContent="flex-end">
                    {isFailedPayment && (
                      <Tooltip title={l(content.genericError, language)}>
                        <img
                          src={errorIcon}
                          alt="error"
                          style={{ width: '1.5rem' }}
                        />
                      </Tooltip>
                    )}
                    <Typography
                      variant="body1"
                      className={[
                        isFailedPayment ? classes.error : classes.price,
                        classes.paymentItem,
                      ].join(' ')}
                      data-payment-item-amount
                    >
                      {payment.paymentType === PAYMENT_TYPE.REFUND ? '+' : '-'}{' '}
                      {formatPriceWithCents(
                        payment.paymentStatus === PAYMENT_STATUS.FAILED ||
                          payment.paymentStatus === PAYMENT_STATUS.VOID
                          ? 0
                          : payment.paymentAmount,
                        sfOrder.currencyCode
                      ) +
                        ' (' +
                        (payment.paymentType === PAYMENT_TYPE.REFUND
                          ? 'Refunded'
                          : payment.paymentStatus) +
                        ')'}
                    </Typography>
                  </Grid>
                </Grid>
              );
            })}
      </Grid>

      <Grid item xs={12} style={{ width: '100%' }}>
        <Divider className={classes.desktopDivider} />
      </Grid>

      {/* {sfOrder.totalPendingCheckPayments > 0 && (
        <Grid item xs={12} container>
          <Grid item xs>
            <Typography
              variant="body1"
              data-cy="data-pendingCheck-label"
              className={classes.total}
            >
              {l(content.pendingCheck, language)}
            </Typography>
          </Grid>
          <Grid item xs container justifyContent="flex-end">
            {error ? (
              <Error />
            ) : (
              <Typography
                variant="body1"
                data-cy="data-pendingCheck-value"
                className={[classes.price, classes.total].join(' ')}
                data-total
              >
                {'- ' +
                  formatPriceWithCents(
                    sfOrder.totalPendingCheckPayments,
                    sfOrder.currencyCode
                  )}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}

      {sfOrder.totalCheckPaymentsRecieved > 0 && (
        <Grid item xs={12} container>
          <Grid item xs>
            <Typography
              variant="body1"
              data-cy="data-successfulCheck-label"
              className={classes.total}
            >
              {l(content.successfulCheck, language)}
            </Typography>
          </Grid>
          <Grid item xs container justifyContent="flex-end">
            {error ? (
              <Error />
            ) : (
              <Typography
                variant="body1"
                data-cy="data-successfulCheck-value"
                className={[classes.price, classes.total].join(' ')}
                data-total
              >
                {'- ' +
                  formatPriceWithCents(
                    sfOrder.totalCheckPaymentsRecieved,
                    sfOrder.currencyCode
                  )}
              </Typography>
            )}
          </Grid>
        </Grid>
      )} */}

      {/* {sfOrder.totalCapturablePayments > 0 && (
        <Grid item xs={12} container>
          <Grid item xs>
            <Typography
              variant="body1"
              data-cy="data-successfulCheck-label"
              className={classes.total}
            >
              {l(content.successfulCheck, language)}
            </Typography>
          </Grid>
          <Grid item xs container justifyContent="flex-end">
            {error ? (
              <Error />
            ) : (
              <Typography
                variant="body1"
                data-cy="data-successfulCheck-value"
                className={[classes.price, classes.total].join(' ')}
                data-total
              >
                {'- ' +
                  formatPriceWithCents(
                    sfOrder.totalCapturablePayments,
                    sfOrder.currencyCode
                  )}
              </Typography>
            )}
          </Grid>
        </Grid>
      )} */}

      {/* Amount Due */}
      <Grid item xs={12} container>
        <Grid item xs>
          <Typography
            variant="body1"
            data-cy="data-amount-due-label"
            className={classes.amountDue}
          >
            {l(content.amountDue, language)}
          </Typography>
        </Grid>
        <Grid item xs container justifyContent="flex-end">
          {error ? (
            <Error />
          ) : (
            <Typography
              variant="body1"
              data-cy="data-amount-due-value"
              className={[classes.price, classes.amountDueValue].join(' ')}
              data-amount-due
            >
              {formatPriceWithCents(balance.amount, balance.currency)}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PaymentHistoryData;
